import { useEffect } from 'react'
import { useStateContext } from '../../context/StateContextAPI'
import { CompactLogo, MainBackgroundVector } from '../../assets'
import { NewsletterForm, PlansContainer } from '../../components'
import useGoogleTag from '../../utils/gtag'
import { useTranslation } from 'react-i18next'
import './fairness.page.css'

const FairnessPage = () => {
    const gtag = process.env.REACT_APP_GOOGLE_TAG || ""
    useGoogleTag(gtag)

    const { setActivePage } = useStateContext()
    const { t } = useTranslation()

    useEffect(() => {
        setActivePage(() => "fairness")
        window.scrollTo(0, 0)
    }, [setActivePage])

    const fairnessPillars = [
        { title: t('fairness.pillar1Title'), text: t('fairness.pillar1Text') },
        { title: t('fairness.pillar2Title'), text: t('fairness.pillar2Text') },
        { title: t('fairness.pillar3Title'), text: t('fairness.pillar3Text') },
        { title: t('fairness.pillar4Title'), text: t('fairness.pillar4Text') },
    ]

    return (
        <div className="fairness-page">
            <img src={MainBackgroundVector} alt="" className="fairness-hero-bg" />

            {/* Hero Section */}
            <div className="fairness-hero-section">
                <span className="fairness-hero-label">{t('fairness.heroLabel')}</span>
                <div className="fairness-hero-title">
                    <span className="head-txt-off-white">{t('fairness.heroTitle1')}</span>
                    <span className="head-txt-primary">{t('fairness.heroTitle2')}</span>
                </div>
                <p className="fairness-hero-subtitle">
                    {t('fairness.heroSubtitle')}
                </p>
            </div>

            {/* Origin Story */}
            <div className="fairness-origin-section">
                <div className="fairness-origin-block">
                    <div className="fairness-origin-label">{t('fairness.originTitle')}</div>
                    <div className="fairness-origin-divider" />
                    <p>{t('fairness.originText')}</p>
                </div>
                <div className="fairness-origin-block">
                    <div className="fairness-origin-label">{t('fairness.evolutionTitle')}</div>
                    <div className="fairness-origin-divider" />
                    <p>{t('fairness.evolutionText')}</p>
                </div>
            </div>

            {/* Fairness That Actually Pays */}
            <div className="fairness-payouts-section">
                <div className="fairness-payouts-header">
                    <span className="head-txt-off-white">{t('fairness.payoutsTitle1')}</span>
                    <span className="head-txt-primary">{t('fairness.payoutsTitle2')}</span>
                </div>

                <div className="fairness-payouts-grid">
                    <div className="fairness-payout-card">
                        <div className="payout-highlight">{t('fairness.payout5xTitle')}</div>
                        <p className="payout-text">{t('fairness.payout5xText')}</p>
                    </div>
                    <div className="fairness-payout-card">
                        <div className="payout-highlight">{t('fairness.payoutDirectTitle')}</div>
                        <p className="payout-text">{t('fairness.payoutDirectText')}</p>
                    </div>
                </div>
            </div>

            {/* What Does Fair Music Streaming Really Mean */}
            <div className="fairness-meaning-section">
                <div className="fairness-meaning-header">
                    <span className="head-txt-off-white">{t('fairness.meaningTitle1')}</span>
                    <span className="head-txt-primary">{t('fairness.meaningTitle2')}</span>
                    <br />
                    <span className="head-txt-off-white">{t('fairness.meaningTitle3')}</span>
                </div>

                <div className="fairness-pillars-grid">
                    {fairnessPillars.map((pillar, index) => (
                        <div className="fairness-pillar-card" key={index}>
                            <div className="fairness-pillar-icon">
                                <img src={CompactLogo} alt="ROKK" />
                            </div>
                            <div className="fairness-pillar-content">
                                <div className="fairness-pillar-title">{pillar.title}</div>
                                <p className="fairness-pillar-text">{pillar.text}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Audience Boxes */}
            <div className="fairness-audience-section">

                {/* For Fans */}
                <div className="fairness-audience-box">
                    <span className="fairness-audience-label">{t('fairness.forFansLabel')}</span>
                    <div className="fairness-audience-title">
                        {t('fairness.forFansTitle')}
                    </div>
                    <div className="fairness-audience-text">
                        <p dangerouslySetInnerHTML={{ __html: t('fairness.forFansText1') }} />
                        <p>{t('fairness.forFansText2')}</p>
                        <p>{t('fairness.forFansText3')}</p>
                    </div>
                </div>

                {/* For Artists */}
                <div className="fairness-audience-box">
                    <span className="fairness-audience-label">{t('fairness.forArtistsLabel')}</span>
                    <div className="fairness-audience-title">
                        {t('fairness.forArtistsTitle')}
                    </div>
                    <div className="fairness-audience-text">
                        <p dangerouslySetInnerHTML={{ __html: t('fairness.forArtistsText1') }} />
                        <p>{t('fairness.forArtistsText2')}</p>
                        <p>{t('fairness.forArtistsText3')}</p>
                    </div>
                    <span className="fairness-audience-cta">
                        {t('fairness.forArtistsCta')}
                    </span>
                </div>
            </div>

            {/* Plans & Pricing */}
            <div className="fairness-plans-section">
                <div className='pricing-section-header' style={{ textAlign: 'center' }}>
                    <span className="head-txt-primary">{t('home.plansAndPricing')}</span>
                </div>
                <div className="flex flex-dir-col gp-20">
                    <span style={{ textAlign: 'center' }}><span style={{ opacity: 0.5 }}>{t('home.plansSaveText')}</span> {t('home.plansAnnual')} <span style={{ opacity: 0.5 }}>{t('home.plansCombine')}</span> {t('home.plansDiscount')}</span>
                </div>
                <PlansContainer />
            </div>

            {/* Newsletter */}
            <div className="fairness-newsletter-section">
                <div className="newsletter-container-pricing-page flex aln-items-cnt">
                    <div className="newsletter-section-header flex flex-dir-col">
                        <span className="head-txt-off-white font-sz-small">{t('newsletter.subscribeTo')}</span>
                        <span className="head-txt-off-white font-sz-small">{t('newsletter.our')}
                            <span className="head-txt-primary">{t('newsletter.rokk')}</span>
                        </span>
                        <span className="head-txt-primary font-sz-small">{t('newsletter.newsletter')}</span>
                    </div>
                    <NewsletterForm />
                </div>
            </div>
        </div>
    )
}

export default FairnessPage
